import * as React from 'react';
import { useEffect, useState } from "react";
import { Chart } from 'chart.js';
import { SiteVariablesPrepared } from "@fluentui/react-northstar";
import { IChartData } from "../chart-types";
import { tooltipAxisXLine, barChartConfigPlugin, axesConfig, setTooltipColorScheme, numberFormat } from "../chart-utils";
import { ChartContainer } from "./chart-container";
import { getText } from "../../../translations";

const BarChart = ({
	title,
	data,
	siteVariables,
	stacked,
	customDataLabel,
	customColors,
	activeSegmentIndex,
	onChangeSegmentIndex,
	clickable,
	theme,
}: {
	title: string;
	data: IChartData;
	siteVariables: SiteVariablesPrepared;
	stacked?: boolean;
	customDataLabel?: boolean;
	customColors?: string[];
	activeSegmentIndex?: number[] | number;
	onChangeSegmentIndex?: Function;
	clickable?: boolean;
	theme?: string;
}) => {
	const { colorScheme, colors, t } = siteVariables;
	const canvasRef = React.useRef<HTMLCanvasElement | null>(null);
	const chartRef = React.useRef<Chart | undefined>();
	const chartId = React.useMemo(
		() => Math.random().toString(36).substr(2, 9),
		[]
	);
	const chartDataPointColors = React.useMemo(
		() => customColors ? customColors : [
			'#27438C',
		],
		[theme]
	);

	const createDataPoints = (): Chart.ChartDataSets[] =>
		Array.from(data.datasets, (set, i) => {
			let dataPointConfig = {
				label: getText(t?.locale, set.label),
				data: set.data,
				borderWidth: 0,
				borderSkipped: false,
				borderColor: colorScheme.default.background,
				hoverBorderColor: chartDataPointColors[i],
				backgroundColor: chartDataPointColors[i],
				hoverBorderWidth: 0,
				hoverBackgroundColor: chartDataPointColors[i],
				pointBorderColor: colorScheme.default.background,
				pointBackgroundColor: colorScheme.default.foreground3,
				pointHoverBackgroundColor: colorScheme.default.foreground3,
				pointHoverBorderColor: chartDataPointColors[i],
				pointHoverBorderWidth: 0,
				borderCapStyle: "round",
				borderJoinStyle: "round",
				pointBorderWidth: 0,
				pointRadius: 0,
				pointHoverRadius: 0,
			};

			return dataPointConfig as any;
		});

	useEffect(() => {
		if (!canvasRef.current) return;
		const ctx = canvasRef.current.getContext("2d");
		if (!ctx) return;

		const config: any = barChartConfigPlugin({ type: "bar", selectedIndex: activeSegmentIndex, customDataLabel: customDataLabel, stacked: stacked, theme: theme });

		config.options.hover.mode = "nearest";
		config.options.scales.xAxes[0].gridLines.offsetGridLines =
			data.datasets.length > 1 && !stacked ? true : false;

		if (stacked) {
			config.options.scales.yAxes[0].stacked = true;
			config.options.scales.xAxes[0].stacked = true;
			config.options.tooltips.callbacks.title = (tooltipItems: any) => {
				let total = 0;
				data.datasets.map((dataset) => {
					const value = dataset.data[tooltipItems[0].index];
					if (typeof value === "number") {
						return (total += value);
					}
				});

				return `${((tooltipItems[0].yLabel / total) * 100).toFixed(0)}% (${numberFormat(tooltipItems[0].yLabel)})`;
			};
		}

		config.options.onHover = (event, chartElement) => {
			event.target.style.cursor = chartElement[0] ? 'pointer' : 'default';
		}

		config.options.onClick = (e, items) => {
			if (items.length === 0 || clickable === false) return;
			const clickedIndex = items[0]._index;
			const clickedDatasetIndex = items[0]._datasetIndex;

			if (items[0]._chart.config.data.datasets.length > 1) {
				if (activeSegmentIndex && (activeSegmentIndex[0] == clickedIndex && activeSegmentIndex[1] == clickedDatasetIndex)) {
					onChangeSegmentIndex(null, null);
				} else {
					onChangeSegmentIndex(clickedIndex, clickedDatasetIndex);
				}
			} else {
				if (activeSegmentIndex == clickedIndex) {
					onChangeSegmentIndex(null);
				} else {
					onChangeSegmentIndex(clickedIndex);
				}
			}
		}

		chartRef.current = new Chart(ctx, {
			...(config as any),
			data: {
				labels: Array.isArray(data.labels)
					? data.labels.map((label) => getText(t?.locale, label))
					: getText(t?.locale, data.labels),
				datasets: [],
			},
		});

		return () => {
			if (!chartRef.current) return;

			chartRef.current.destroy();
		};
	}, [data, activeSegmentIndex]);

	useEffect(() => {
		if (!chartRef.current) return;
		if (!canvasRef.current) return;
		const ctx = canvasRef.current.getContext("2d");
		if (!ctx) return;

		if (data.datasets.length > 1) {
			const updatedDatasets = data.datasets.map((dataset, datasetIndex) => {
				let updatedColors = dataset.data.map((_, dataIndex) => {
					return activeSegmentIndex && (activeSegmentIndex[0] === dataIndex && activeSegmentIndex[1] == datasetIndex) ? customColors[datasetIndex] : theme === "dark" ? "#525252" : "#f5f5f5";
				});

				if (activeSegmentIndex) {
					if (activeSegmentIndex[0] == null && activeSegmentIndex[1] == null) {
						const colorsForBars = dataset.data.map((_, idx) => {
							return customColors[datasetIndex];
						});

						updatedColors = colorsForBars;
					}
				}

				return {
					...dataset,
					data: dataset.data,
					label: getText(t?.locale, dataset.label),
					backgroundColor: updatedColors,
					hoverBackgroundColor: updatedColors,
				};
			});

			chartRef.current.data.datasets = updatedDatasets;

			if (activeSegmentIndex != null && (activeSegmentIndex[0] != null && activeSegmentIndex[1] != null)) {
				chartRef.current.options.animation.duration = 0;
			}
		} else {
			let updatedColors = data.datasets[0].data.map((_, index) => activeSegmentIndex === index ? chartDataPointColors[0] : theme === "dark" ? "#525252" : "#f5f5f5");

			if (activeSegmentIndex == null)
				updatedColors = data.datasets[0].data.map((_, index) => chartDataPointColors[0]);

			chartRef.current.data.datasets = [
				{
					label: getText(t?.locale, data.datasets[0].label),
					data: data.datasets[0].data,
					backgroundColor: updatedColors,
					hoverBackgroundColor: updatedColors,
				},
			];

			if (activeSegmentIndex != null) {
				chartRef.current.options.animation.duration = 0;
			}
		}

		chartRef.current.update();
	}, [activeSegmentIndex]);

	/**
	 * Theme updates
	 */
	useEffect(() => {
		if (!chartRef.current) return;
		if (!canvasRef.current) return;
		const ctx = canvasRef.current.getContext("2d");
		if (!ctx) return;
		// Apply new colors scheme for data points
		activeSegmentIndex == null ? chartRef.current.data.datasets = createDataPoints() : null;
		// Update tooltip colors scheme
		setTooltipColorScheme({
			chart: chartRef.current,
			siteVariables,
			chartDataPointColors,
		});
		// Update axeses
		axesConfig({ chart: chartRef.current, ctx, colorScheme });

		chartRef.current.update();
	}, [theme, data, activeSegmentIndex]);

	function onLegendClick(datasetIndex: number) {
		if (!chartRef.current) return;
		chartRef.current.data.datasets![datasetIndex].hidden = !chartRef.current
			.data.datasets![datasetIndex].hidden;
		chartRef.current.update();
	}

	return (
		<ChartContainer
			siteVariables={siteVariables}
			data={data}
			chartDataPointColors={chartDataPointColors}
			onLegendClick={onLegendClick}
			hideLegend={true}
		>
			<canvas
				id={chartId}
				ref={canvasRef}
				tabIndex={0}
				style={{
					userSelect: "none",
				}}
				aria-label={title}
			>
				{data.datasets.map((set, setKey) =>
					(set.data as number[]).forEach((item: number, itemKey: number) => (
						// Generated tooltips for screen readers
						<div key={itemKey} id={`${chartId}-tooltip-${setKey}-${itemKey}`}>
							<p>{item}</p>
							<span>
								{getText(t?.locale, set.label)}: {set.data[itemKey]}
							</span>
						</div>
					))
				)}
			</canvas>
		</ChartContainer>
	);
};

export default BarChart;